/**
 * useCellRange — spreadsheet-style block selection over a table of inputs.
 *
 * Press on any cell, drag across to take in more columns and down to take in
 * more rows, release: the block stays highlighted and can be copied (the
 * caller's Copy button, or Ctrl/Cmd+C). A plain click never becomes a range —
 * the block only starts once the pointer enters a DIFFERENT cell with the
 * button held — so clicking into a box to edit works exactly as before.
 *
 * A press that lands INSIDE a text box stays a text drag until the pointer
 * leaves the ROW: dragging past a narrow box's edge to select to the end of its
 * text keeps working, and "qty, across to description, then down" still gives
 * the qty + description block (the column the drag last reached is remembered,
 * and the block starts on the row change).
 *
 * Why: every row in these grids is a set of <input>s, and a browser selection
 * cannot span inputs. Dragging "from the qty down the description column" had
 * no way to work natively; this gives it one.
 *
 * Coordinates are the CALLER's: `r` is whatever row index it uses (the takeoff
 * uses the items[] index), `c` the column ordinal among the cells that got
 * rc(). A cell that spans the whole row (a section header) passes c = null:
 * it lights up whenever its row is inside the block and never changes the
 * block's columns. Several tables can share one hook — `table` keeps them
 * apart, and a drag that starts in one never extends into another.
 *
 *   const { range, rc, clearRange, copied, markCopied } = useCellRange({ id: "takeoff", onCopy });
 *   <td {...rc("grid", i, 3, { padding: 2 })}>…</td>
 *   <div data-rsel="takeoff">…the tables and the copy pill…</div>
 *
 * `data-rsel` marks the region a range belongs to: a mousedown anywhere else
 * clears it, so a stale block never lingers under an unrelated click.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

const HL = { background: "rgba(96,165,250,.25)", boxShadow: "inset 0 0 0 1px rgba(96,165,250,.75)" };

// Controls that need the mousedown for themselves — never a range origin.
const isControl = (el) => !!(el && el.closest && el.closest("select,button,a,input[type=checkbox],input[type=radio],input[type=file],label"));
// A box the tech types in — a press here is a text gesture first.
const TEXT_BOX = "textarea,[contenteditable=true],input:not([type=checkbox]):not([type=radio]):not([type=file]):not([type=button]):not([type=submit]):not([type=range]):not([type=color])";
const isTextBox = (el) => !!(el && el.closest && el.closest(TEXT_BOX));

export function normRange(r) {
  if (!r) return null;
  return { table: r.table, r0: Math.min(r.r0, r.r1), r1: Math.max(r.r0, r.r1), c0: Math.min(r.c0, r.c1), c1: Math.max(r.c0, r.c1) };
}

export function useCellRange({ id, onCopy }) {
  const [range, setRange] = useState(null);       // { table, r0, r1, c0, c1 } as dragged (unordered)
  const [copied, setCopied] = useState(false);    // "✓ Copied" flash for the block on screen
  const rangeRef = useRef(null);
  const dragRef = useRef(null);                   // { table, r, c, lastC, active, fromText } while the button is down
  const onCopyRef = useRef(onCopy);
  const copyTimerRef = useRef(null);
  useEffect(() => { rangeRef.current = range; }, [range]);
  useEffect(() => { onCopyRef.current = onCopy; }, [onCopy]);

  // Drop the drag origin, however the press ended. Also the answer to a
  // native drag-and-drop (text dragged out of a box, a ⠿ row drag): the
  // browser fires dragstart and never a mouseup, so without this the origin
  // would survive and the next hover would light a block with no button held.
  const disarm = useCallback(() => {
    const d = dragRef.current;
    if (!d) return;
    if (d.active) document.body.style.userSelect = "";
    dragRef.current = null;
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      // A click outside this hook's region drops the block (its pill is inside
      // the region, so pressing Copy does not).
      if (!rangeRef.current) return;
      const inside = e.target && e.target.closest && e.target.closest(`[data-rsel="${id}"]`);
      if (!inside) setRange(null);
    };
    const onKey = (e) => {
      if (!rangeRef.current) return;
      if (e.key === "Escape") { setRange(null); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
        // The native copy wins whenever the tech has something of their own
        // selected: text inside a box (a number box reports null for its
        // selection — treat that as "the browser knows better" too), or plain
        // page text anywhere.
        const el = document.activeElement;
        const editable = el && (el.tagName === "TEXTAREA" || el.isContentEditable
          || (el.tagName === "INPUT" && !/^(checkbox|radio|file|button|submit|range|color)$/i.test(el.type || "")));
        if (editable) {
          let own = null;
          try { own = el.selectionStart == null ? null : el.selectionStart !== el.selectionEnd; } catch { own = null; }
          if (own !== false) return;
        }
        try { const s = window.getSelection(); if (s && !s.isCollapsed && String(s).trim()) return; } catch { /* ignore */ }
        e.preventDefault();
        if (onCopyRef.current) onCopyRef.current(normRange(rangeRef.current));
      }
    };
    document.addEventListener("mouseup", disarm);
    document.addEventListener("dragstart", disarm);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mouseup", disarm);
      document.removeEventListener("dragstart", disarm);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      document.body.style.userSelect = "";
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, [id, disarm]);

  const n = normRange(range);
  const inRange = useCallback((table, r, c) => !!(n && n.table === table && r >= n.r0 && r <= n.r1 && (c == null || (c >= n.c0 && c <= n.c1))), [n]);

  /** Props for one cell: the mouse hooks plus the highlight merged into `style`. */
  const rc = useCallback((table, r, c, style) => ({
    onMouseDown: (e) => {
      if (e.button !== 0 || isControl(e.target)) return;
      dragRef.current = { table, r, c: c == null ? null : c, lastC: c == null ? null : c, active: false, fromText: isTextBox(e.target) };
      // A new press anywhere on a cell starts fresh — the old block is gone.
      if (rangeRef.current) setRange(null);
      setCopied(false);
    },
    onMouseEnter: (e) => {
      const d = dragRef.current;
      if (!d || d.table !== table) return;
      // No button held: the press ended somewhere we never heard about (a
      // native drag ate the mouseup, or it was released off-window).
      if (!(e.buttons & 1)) { disarm(); return; }
      // A whole-row cell keeps whatever column the drag last had; the first
      // real column a header-started drag reaches becomes its origin column.
      if (c != null) { if (d.c == null) d.c = c; d.lastC = c; }
      const cc = c == null ? d.lastC : c;
      if (!d.active) {
        if (d.r === r && (c == null || d.c === c)) return;   // still on the origin cell: a click, not a drag
        if (d.fromText && d.r === r) return;                  // a text drag inside the row (past a box's edge) — leave it to the browser
        d.active = true;
        // Leave the box the press landed in, drop its native text selection,
        // and stop the page selecting text while the block is dragged out.
        try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch { /* ignore */ }
        try { const s = window.getSelection(); if (s) s.removeAllRanges(); } catch { /* ignore */ }
        document.body.style.userSelect = "none";
      }
      if (d.c == null || cc == null) return;   // header to header only: no column yet
      setCopied(false);
      setRange({ table, r0: d.r, r1: r, c0: d.c, c1: cc });
    },
    style: inRange(table, r, c) ? { ...(style || {}), ...HL } : style,
  }), [inRange, disarm]);

  const clearRange = useCallback(() => setRange(null), []);
  // "✓ Copied" for a moment, then the block goes — but only THAT block: a new
  // one dragged in the meantime is left alone.
  const markCopied = useCallback(() => {
    const at = rangeRef.current;
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      copyTimerRef.current = null;
      setCopied(false);
      if (rangeRef.current === at) setRange(null);
    }, 1200);
  }, []);
  return { range: n, rc, clearRange, inRange, copied, markCopied };
}

/**
 * Block → tab-separated text (pastes as columns into Sheets/Excel; a single
 * column pastes as plain lines). `rows` is the ordered list of row indexes to
 * consider; `cellText(r, c)` returns the text of one cell or null to skip the
 * whole row (a row on another table, a hidden row).
 */
export function rangeToText(rng, rows, cellText) {
  if (!rng) return "";
  const out = [];
  for (const r of rows) {
    if (r < rng.r0 || r > rng.r1) continue;
    const cells = [];
    let skip = false;
    for (let c = rng.c0; c <= rng.c1; c++) {
      const t = cellText(r, c);
      if (t === null) { skip = true; break; }
      cells.push(String(t == null ? "" : t).replace(/[\t\r\n]+/g, " ").trim());
    }
    if (skip) continue;
    out.push(cells.join("\t"));
  }
  return out.join("\n");
}

/** Copy the text with a fallback for browsers that refuse the async clipboard. */
export async function copyToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return !!ok;
    } catch { return false; }
  }
}

/**
 * The floating "N rows × M columns — Copy" pill. Sits inside the range's
 * region (data-rsel) so pressing Copy does not clear the block. Same dark
 * palette both editors use. `bottom` lets a caller that already has a toast
 * at the default spot (the quote editor's mirror toast at 76) stack above it;
 * the z-index sits UNDER the editors' modal overlays on purpose.
 */
export function RangePill({ rsel, rows, cols, onCopy, onClear, done, note, bottom = 76, zIndex = 50 }) {
  const P = { surface: "#161a20", blue: "#60a5fa", text: "#e8eaf0", muted: "#8892a4", lime: "#a5ce3a", amber: "#f59e0b" };
  const label = done ? "✓ Copied" : note ? note : `▦ ${rows} row${rows === 1 ? "" : "s"} × ${cols} column${cols === 1 ? "" : "s"}`;
  return (
    <div data-rsel={rsel} style={{ position: "fixed", bottom, left: "50%", transform: "translateX(-50%)", zIndex, display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderRadius: 999, background: P.surface, border: `1px solid ${done ? P.lime : note ? P.amber : P.blue}`, boxShadow: "0 6px 24px rgba(0,0,0,.45)", fontSize: 12.5, color: note && !done ? P.amber : P.text, whiteSpace: "nowrap", fontFamily: "inherit" }}>
      <span>{label}</span>
      <button type="button" onClick={onCopy}
        style={{ background: P.lime, border: `1px solid ${P.lime}`, borderRadius: 8, color: "#0d0f12", fontWeight: 700, fontSize: 12.5, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit" }}>
        📋 Copy
      </button>
      <span style={{ color: P.muted }}>or Ctrl+C · Esc</span>
      <span onClick={onClear} title="Clear the selection" style={{ cursor: "pointer", color: P.muted }}>✕</span>
    </div>
  );
}
